import { Component, HostBinding } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { ApiService } from "src/app/core/api.service";
import { Base64Service } from "src/app/core/base64.service";
import { ValidateService } from "src/app/core/validate.service";

interface Fields {
  emailAddress: string;
}

type Field = keyof Fields;

type FormControls<T> = {
  [K in keyof T]: FormControl<T[K] | null>;
};

type Page = 'enter-email' | 'select-mode' | 'password-reset-link-sent' | 'password-hint-sent' | 'error';

@Component({
  templateUrl: './forgot-password.component.html',
  styleUrls: ['./forgot-password.component.scss'],
})
export class ForgotPasswordComponent {
  protected touched: boolean = false;
  protected hasHint: boolean = false;
  protected hasReset: boolean = false;
  protected resetCode: string = '';
  protected page: Page = 'enter-email';
  protected spinner: boolean = false;
  protected form: FormGroup<FormControls<Fields>>;

  constructor(
    private base64Service: Base64Service,
    private apiService: ApiService,
    private router: Router,
    private fb: FormBuilder,
    private validateService: ValidateService
  ) {}

  ngOnInit() {
    this.form = this.fb.group({
      emailAddress: ['', Validators.compose([
        Validators.required,
        this.customEmailValidator.bind(this)
      ])],
    });
  }

  private customEmailValidator(control: FormControl): { [key: string]: boolean } | null {
    const value = control.value?.trim();
    return this.validateService.isEmail(value) ? null : { invalidEmail: true };
  }  

  private async getPasswordForgotList(emailB64: string): Promise<any> {
    return await this.apiService.send('userpasswordforgotlist', {
        email_b64: emailB64
    });
  }

  private async requestPasswordReset(email: string): Promise<any> {
    return await this.apiService.send('requestpassreset', { email });
  }

  private async requestPasswordHint(email: string): Promise<any> {
    return await this.apiService.send('requesthint', { email });
  }

  protected getErrorMessage(field: Field) {
    if (!this.form.controls[field].touched) return;

    if (this.form.controls[field].hasError('required')) {
      return 'This field is required';
    }
    else if (this.form.controls[field].hasError('invalidEmail')) {
      return 'Enter a valid email address';
    }
  }

  protected get canContinue() {
    return this.form.valid && !this.spinner;
  }

  protected get emailAddress() {
    return this.form.controls.emailAddress.value?.trim();
  }

  // UI handlers
  protected onClickBackToSignIn = () => {
    this.router.navigateByUrl('/login');
  }

  protected async emailResetSubmit() {
    const response =  await this.requestPasswordReset(this.emailAddress);
    this.resetCode = response.code;
    this.page = 'password-reset-link-sent';
  }

  public async emailHintSubmit() {
    await this.requestPasswordHint(this.emailAddress);
    this.page = 'password-hint-sent';
  }

  protected checkMethods = async () => {
    try {
      this.spinner = true;
        const emailB64 = this.base64Service.encode(this.emailAddress.toLowerCase());
        const result = await this.getPasswordForgotList(emailB64);
        this.hasHint = (result.has_passwordhint > 0);
        this.hasReset = (result.has_passwordreset > 0);
        this.page = 'select-mode';  
    } catch (err) {
      this.page = 'error';
    } finally {
      this.spinner = false;
    }
  }
}